import React, { useState } from 'react';
import { Button } from 'antd';
import { ShareAltOutlined } from '@ant-design/icons';
import { LogEntryWithType, CallLog, TextLog, VoiceMailLog } from '@/types/TInbox';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import AudioPlayer from './AudioPlayer';
import TranscriptViewer from './TranscriptView';
import CallAnalytics from './CallAnalysis';
import TextMessageDisplay from './TextMessage';
import IntakeFormsDisplay from './IntakeFormView';
import PricingDetailsTab from './PricingDetailsTab';
import IntakeResponseTab from './IntakeResponseTab';
import SmartCallAnalysisTab from './SmartCallAnalysisTab';
import ShareCallLogModal from '../../CallLogs/Modals/ShareCallLogModal';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent } from '@/store/account/selector';
import { ECallRecordingType } from 'src/enums/ECallRecording';
import { renderPSTDate, safeJSONParse } from '@/utils/helper';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';

interface LogDetailsProps {
	log: LogEntryWithType | null;
	debrand?: boolean;
	isNoviUser?: boolean;
}

const LogDetails: React.FC<LogDetailsProps> = ({ log, debrand = false }) => {
	const selectedAgent = useAppSelector(getSelectedAgent);
	const [activeCallTab, setActiveCallTab] = useState('recording');
	const dispatch = useAppDispatch();

	const isFormResponsesEnabled = useFeatureFlag(FeatureFlag.FORM_RESPONSES_PAGE);

	if (!log) {
		return (
			<div className="h-full flex items-center justify-center bg-white">
				<div className="text-center">
					<h3 className="text-lg font-medium text-gray-900 mb-2">
						Select a message to view details
					</h3>
					<p className="text-gray-500">
						Choose a conversation from the list to see the full content.
					</p>
				</div>
			</div>
		);
	}

	const getInitials = (name?: string) => {
		if (!name) {
			return '?';
		}
		return name
			.split(' ')
			.map((n) => n[0])
			.join('')
			.toUpperCase();
	};

	const getDisplayName = (name?: string) => {
		return name || 'Unknown Caller';
	};

	// Helper functions for checking data availability
	const hasShareableLink = () => {
		return !!(log.originalLog as any)?.shareableLink;
	};

	const hasPricingData = () => {
		if (debrand) {
			return false;
		}
		const record = log.originalLog as any;
		return !!(
			record?.costCoveredByFreeMinutes ||
			record?.aiCallCost ||
			record?.transferredCallCost
		);
	};

	const hasIntakeResponse = () => {
		try {
			const record = log.originalLog as any;
			const intakeResponses = record?.intakeResponses;
			if (!intakeResponses) {
				return false;
			}

			const parsedIntakeResponses = safeJSONParse(intakeResponses, []);

			if (Array.isArray(parsedIntakeResponses)) {
				return (
					parsedIntakeResponses.length > 0 && Object.keys(parsedIntakeResponses[0] || {}).length > 0
				);
			} else if (typeof parsedIntakeResponses === 'object' && parsedIntakeResponses !== null) {
				return Object.keys(parsedIntakeResponses).length > 0;
			}
			return false;
		} catch {
			return false;
		}
	};

	const hasSmartAnalysis = () => {
		if (log.type !== 'call') {
			return false;
		}
		const callData = log.originalLog as CallLog;
		return !!(
			callData?.callSummary ||
			callData?.callScore ||
			callData?.callSentiments ||
			(callData as any)?.postCallAnalysis
		);
	};

	const handleShare = () => {
		const record = log.originalLog as any;
		dispatch(
			openModal({
				modalId: MODAL_IDS.SHARE_CALL_LOG_MODAL,
				data: { shareableLink: record.shareableLink, debrand },
			}),
		);
	};

	// Get available tabs based on log type and data availability
	const getAvailableTabs = () => {
		const tabs = [];

		if (log.type === 'call') {
			tabs.push(
				{ key: 'recording', label: 'Recording & Transcript' },
				{ key: 'analysis', label: 'Summary & Analysis' },
			);

			if (hasSmartAnalysis()) {
				tabs.push({ key: 'smart-analysis', label: 'Smart Call Analysis' });
			}
		}

		if (hasPricingData()) {
			tabs.push({ key: 'pricing', label: 'Pricing Details' });
		}

		if (hasIntakeResponse()) {
			tabs.push({ key: 'intake-response', label: 'Intake Form Response' });
		}

		return tabs;
	};

	const availableTabs = getAvailableTabs();

	return (
		<div className="h-full bg-white overflow-y-auto">
			{/* Header */}
			<div className="px-6 py-3 border-b border-gray-200 bg-white">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
							<span className="text-white font-semibold text-sm">
								{getInitials(
									log.type === 'text' || log.type === 'voicemail'
										? selectedAgent?.name
										: log.contact.name,
								)}
							</span>
						</div>
						<div className="flex-1">
							<div className="flex items-center gap-3">
								<span className="text-lg font-semibold text-gray-900">
									{log.type === 'text' || log.type === 'voicemail'
										? selectedAgent?.name
										: getDisplayName(log.contact.name)}
								</span>
								<div className="flex items-center gap-2 text-xs text-gray-500">
									<span>•</span>
									{log.type === 'call' && (
										<>
											<span className="text-md font-semibold">{log.contact.phone}</span>
											<span>•</span>
										</>
									)}
									<span className="text-xs text-gray-500">
										{renderPSTDate(log.date, selectedAgent?.time_zone)}
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="flex items-center gap-2">
						{/* Share Button */}
						<Button
							icon={<ShareAltOutlined />}
							disabled={!hasShareableLink()}
							danger={!hasShareableLink()}
							onClick={handleShare}
							className="shadow-sm"
						>
							Share
						</Button>
					</div>
				</div>
			</div>
			{/* Content */}
			<div className="px-6 py-4">
				{/* CALL LOGS */}
				{log.type === ECallRecordingType.CALL && (
					<div className="mb-6">
						{(() => {
							const callData = log.originalLog as CallLog;

							return (
								<>
									{/* Call Tabs */}
									{availableTabs.length > 0 && (
										<div className="mb-5">
											<div className="flex border-b border-gray-200 overflow-x-auto">
												{availableTabs.map((tab) => (
													<button
														key={tab.key}
														onClick={() => setActiveCallTab(tab.key)}
														className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
															activeCallTab === tab.key
																? 'border-blue-500 text-blue-600'
																: 'border-transparent text-gray-500 hover:text-gray-700'
														}`}
													>
														{tab.label}
													</button>
												))}
											</div>
										</div>
									)}

									{/* Tab Content */}
									{activeCallTab === 'recording' && (
										<div>
											<AudioPlayer
												callSid={callData.callSid}
												isRecorded={callData.isRecorded === 'true'}
												type={ECallRecordingType.CALL}
											/>
											<TranscriptViewer transcript={callData.transcript} />
										</div>
									)}

									{activeCallTab === 'analysis' && (
										<CallAnalytics
											callSummary={callData.callSummary}
											callScore={callData.callScore}
											callSentiments={callData.callSentiments}
										/>
									)}

									{activeCallTab === 'smart-analysis' && hasSmartAnalysis() && (
										<SmartCallAnalysisTab
											callData={callData}
											postCallAnalysis={(callData as any).postCallAnalysis}
										/>
									)}

									{activeCallTab === 'pricing' && hasPricingData() && (
										<PricingDetailsTab
											duration={callData.duration}
											transferredCallDuration={(callData as any).transferredCallDuration}
											costCoveredByFreeMinutes={(callData as any).costCoveredByFreeMinutes}
											aiCallCost={(callData as any).aiCallCost}
											transferredCallCost={(callData as any).transferredCallCost}
										/>
									)}

									{activeCallTab === 'intake-response' && hasIntakeResponse() && (
										<IntakeResponseTab
											intakeResponses={(callData as any).intakeResponses}
											intakeMetadata={(callData as any).intakeMetadata}
											agentId={selectedAgent.number}
											callSid={(callData as any).callSid}
											isNewStatusSystemEnabled={isFormResponsesEnabled}
										/>
									)}
								</>
							);
						})()}
					</div>
				)}

				{/* VOICEMAIL LOGS */}
				{log.type === ECallRecordingType.VOICEMAIL && (
					<div>
						{(() => {
							const vmData = log.originalLog as VoiceMailLog;
							return (
								<div>
									<div className="p-4 bg-gray-50 rounded-lg mb-4">
										<AudioPlayer
											callSid={vmData.callSid}
											isRecorded={true}
											type={ECallRecordingType.VOICEMAIL}
											voicemailRecordStarted={vmData.voicemailRecordStarted}
											voicemailRecordDuration={vmData.voicemailRecordDuration}
										/>
									</div>
									<TranscriptViewer
										transcript={vmData.voicemail_transcript}
										height="500px"
										isVoicemail={true}
									/>
								</div>
							);
						})()}
					</div>
				)}

				{/* TEXT LOGS */}
				{log.type === 'text' && (
					<div>
						{(() => {
							const textData = log.originalLog as TextLog;
							return (
								<TextMessageDisplay
									from={selectedAgent?.name || 'My First AI Agent'}
									textLog={textData}
								/>
							);
						})()}
					</div>
				)}

				{/* INTAKE FORMS */}
				{log.type === 'form' && (
					<div className="h-[calc(100vh-140px)]">
						{log.formGroup ? (
							<div>
								<IntakeFormsDisplay formGroup={log.formGroup} agentId={selectedAgent.number} />
							</div>
						) : (
							<div className="text-center py-8">
								<div className="text-4xl text-gray-400 mb-4">📋</div>
								<p className="text-gray-500 mb-2">No intake form data available</p>
								<div className="text-xs text-red-600 bg-red-50 p-3 rounded border">
									<p>Debug: log.formGroup is {log.formGroup ? 'present' : 'missing'}</p>
									<p>Log type: {log.type}</p>
									<p>Original log: {log.originalLog ? 'present' : 'missing'}</p>
								</div>
							</div>
						)}
					</div>
				)}
			</div>

			<ShareCallLogModal />
		</div>
	);
};

export default LogDetails;
